<template>
  <span
    class="picture-priority"
    @click.stop
    @keydown.stop
  >
    <label
      class="picture-priority__label"
      :class="{ 'picture-priority__label--checked': checked }"
      :title="saving ? 'Saving selection...' : 'Show this picture first on your public profile. Privacy settings still apply.'"
    >
      <input
        type="checkbox"
        :checked="checked"
        :disabled="saving || !mediaId"
        aria-label="Show this picture first on your public profile"
        @change="change"
      />
    </label>
    <input
      v-if="checked"
      class="picture-priority__order"
      type="number"
      min="1"
      max="9999"
      step="1"
      inputmode="numeric"
      :value="priority"
      :disabled="saving"
      aria-label="Public picture order"
      title="Public picture order: lower numbers appear first"
      @change="changePriority"
      @keydown.enter.prevent="$event.target.blur()"
    />
    <v-snackbar
      v-model="error"
      color="error"
      :timeout="6000"
    >
      {{ errorMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="error = false"
        >Close</v-btn>
      </template>
    </v-snackbar>
  </span>
</template>

<script>
import axios from 'axios'

export default {
  props: {
    record: { type: Object, required: true },
    index: { type: Number, default: 0 },
  },
  data: () => ({ error: false, errorMessage: '' }),
  computed: {
    mediaId() { return (this.record.files_link_ids || [])[this.index] },
    checked() { return !!(this.record.public_featured_media || [])[this.index] },
    priority() { return Number((this.record.public_priority_media || [])[this.index]) || 1 },
    saving() { return !!(this.record.prioritySaving || {})[this.mediaId] },
  },
  methods: {
    change(event) {
      const { target } = event
      const selected = target.checked
      target.checked = this.checked
      this.save(selected)
    },
    changePriority(event) {
      const { target } = event
      const value = Number(target.value)
      target.value = this.priority
      if (!Number.isInteger(value) || value < 1 || value > 9999) {
        this.errorMessage = 'Enter a whole number from 1 to 9999.'
        this.error = true

        return
      }
      if (value !== this.priority) this.save(true, value)
    },
    async save(featured, priority) {
      if (this.saving || !this.mediaId) return

      // Capture the selected media even if the user moves the carousel while saving.
      const { mediaId, index, record } = this
      if (!record.prioritySaving) this.$set(record, 'prioritySaving', {})
      this.$set(record.prioritySaving, mediaId, true)
      this.error = false
      try {
        const { data } = await axios.post(`${process.env.VUE_APP_ROOT_API}/updateFileInfos`, {
          operation: 'picture_priority',
          media_id: mediaId,
          featured,
          ...(priority === undefined ? {} : { priority }),
        }, { headers: { Authorization: this.$store.state.jwtToken } })
        if (Number(data.media_id) !== Number(mediaId) || data.featured !== featured) throw new Error('Invalid selection response')
        if (featured && (!Number.isInteger(data.priority) || data.priority < 1 || data.priority > 9999 || (priority !== undefined && data.priority !== priority))) throw new Error('Invalid order response')
        if (!record.public_featured_media) this.$set(record, 'public_featured_media', [])
        if (!record.public_priority_media) this.$set(record, 'public_priority_media', [])
        this.$set(record.public_featured_media, index, data.featured)
        this.$set(record.public_priority_media, index, data.priority)
      } catch (error) {
        this.errorMessage = (error.response && error.response.data.message) || 'Could not save picture order. Please try again.'
        this.error = true
      } finally {
        this.$set(record.prioritySaving, mediaId, false)
      }
    },
  },
}
</script>

<style scoped>
.picture-priority {
  position: absolute; top: 8px; right: 8px; z-index: 6;
  display: inline-flex; align-items: center; height: 30px;
  background: rgba(255, 255, 255, .8); color: #514b60;
  border: 1px solid rgba(40, 34, 54, .16); border-radius: 5px;
}
.picture-priority__label {
  display: inline-flex; align-items: center; justify-content: center; width: 30px; height: 28px;
  cursor: pointer;
}
.picture-priority:focus-within { outline: 2px solid #9155fd; outline-offset: 2px; }
.picture-priority__label input { width: 14px; height: 14px; accent-color: #9155fd; cursor: pointer; }
.picture-priority__label input:disabled { cursor: wait; }
.picture-priority__order {
  width: 45px; height: 22px; padding: 0 4px; border-left: 1px solid rgba(40, 34, 54, .16);
  color: #514b60; font-size: 12px; text-align: center; outline: none; appearance: textfield; -moz-appearance: textfield;
}
.picture-priority__order::-webkit-inner-spin-button,
.picture-priority__order::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
.picture-priority__order:disabled { opacity: .6; }
</style>
